import { INSPECTION_RESULT_LABELS } from './inspection-labels';

/**
 * The notice a submitted inspection gets when the API says the crew's overall
 * result understates its own checklist (api#922, decision D6).
 *
 * THE API DECIDES, NOT THIS FILE. POST and PATCH `/casing/inspection-submissions`
 * return `warnings: ['overall_result_disagrees']` whenever the crew's
 * `overall_result` is LESS severe than the server's `derived_result` — a Pass
 * over a minor item, a Pass over a fail, AND a Conditional over a fail (PAR,
 * 2026-07-22, three fails stored Conditional). So the wording cannot say "the
 * overall result was Pass": a third of the cases it fires on were not.
 *
 * Nothing here compares the items to the result. A checklist with no answered
 * item has NO server result (`derived_result` null) and the API sends no
 * warning — a client-side "every item blank, so it passed" would be the second
 * answer to one question, and the wrong one.
 *
 * The record is never refused and never rewritten: the crew's value is what
 * they signed and the server's sits beside it. Hence non-blocking, and hence
 * "the record keeps both".
 */
export const OVERALL_RESULT_DISAGREES = 'overall_result_disagrees';

/** What the items say, from the API's derived result. */
const DERIVED_PHRASE: Record<string, string> = {
  fail: 'One or more items failed',
  conditional: 'One or more items were marked minor',
};

/**
 * The notice text for a write's response body, or null when the API did not
 * warn. Takes the axios `res.data` envelope (`{ success, data, warnings }`).
 */
export function inspectionDisagreementNotice(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const warnings = (body as { warnings?: unknown }).warnings;
  if (!Array.isArray(warnings) || !warnings.includes(OVERALL_RESULT_DISAGREES)) return null;

  const row = ('data' in body ? (body as { data?: unknown }).data : null) as
    | Record<string, unknown>
    | null
    | undefined;
  const crewKey = row?.overallResult ?? row?.overall_result;
  const derivedKey = row?.derivedResult ?? row?.derived_result;
  const crew = typeof crewKey === 'string' ? INSPECTION_RESULT_LABELS[crewKey] : undefined;
  const items = typeof derivedKey === 'string' ? DERIVED_PHRASE[derivedKey] : undefined;

  // Both halves named only when the API returned both. The warning is the
  // finding; the specifics are a courtesy, and never guessed.
  if (crew && items) {
    return `${items} but the overall result was ${crew} — the record keeps both.`;
  }
  return 'The checklist items are more serious than the overall result recorded — the record keeps both.';
}
